import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FilePlus2, Mic, FileArchive, Loader2, CheckCircle2 } from "lucide-react";
import PageHeader from "../../components/PageHeader";
import axiosInstance from "../../services/api/axios";
import { getPresignedUploadUrls, uploadFilesToS3 } from "../../utils/fileUtils";
import VoiceRecorder from "../../components/marketplace/VoiceRecorder";
import { getSkills, createProject, approveProject } from "../../services/archMarketplace";

const fmtINR = (n) => (n == null ? "—" : `₹${Number(n).toLocaleString("en-IN")}`);

async function uploadOne(file, folder) {
  const presigned = await getPresignedUploadUrls([file], folder, axiosInstance);
  const p = presigned[0];
  await uploadFilesToS3([{ file, upload_url: p.upload_url, content_type: p.content_type || file.type }]);
  return p.s3_key;
}

function NewProject() {
  const navigate = useNavigate();
  const [skills, setSkills] = useState([]);
  const [selected, setSelected] = useState([]);
  const [projectName, setProjectName] = useState("");
  const [description, setDescription] = useState("");
  const [voiceFile, setVoiceFile] = useState(null);
  const [zipFile, setZipFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [priced, setPriced] = useState(null); // the created project (PRICED)
  const [approving, setApproving] = useState(false);

  useEffect(() => {
    getSkills("ARCHITECTURE")
      .then((s) => setSkills(Array.isArray(s) ? s : []))
      .catch(() => setSkills([]));
  }, []);

  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const canSubmit = projectName.trim() && description.trim() && selected.length > 0 && !submitting;

  const breakdownLines = useMemo(() => priced?.priceBreakdown?.lines || [], [priced]);

  const handleGetPrice = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      let voiceKey = null;
      let zipKey = null;
      if (voiceFile) voiceKey = await uploadOne(voiceFile, "voice_notes");
      if (zipFile) zipKey = await uploadOne(zipFile, "zips");
      const project = await createProject({
        projectName: projectName.trim(),
        description: description.trim(),
        requiredSkillIds: selected,
        voiceNoteKey: voiceKey,
        zipKey,
      });
      setPriced(project);
      toast.success("Estimate ready — review and approve to find a match.");
    } catch (e) {
      toast.error(e?.detail || e?.response?.data?.detail || "Could not price the project.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async () => {
    setApproving(true);
    try {
      await approveProject(priced.archProjectId);
      toast.success("Approved! We're finding the best match now.");
      navigate(`/architect/projects/${priced.archProjectId}`);
    } catch (e) {
      const msg = e?.detail || e?.response?.data?.detail || "Could not approve.";
      toast.error(msg);
      if (String(msg).toLowerCase().includes("balance")) navigate("/architect/wallet");
    } finally {
      setApproving(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="New Project"
        subtitle="Describe the work — you'll get an instant estimate"
        icon={FilePlus2}
        backTo="/architect/dashboard"
        backLabel="Back to projects"
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form */}
        <div className="lg:col-span-2 space-y-5 rounded-lg border border-gray-200 bg-white p-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project Name *</label>
            <input
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              disabled={!!priced}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-flamingo-300 disabled:bg-gray-50"
              placeholder="e.g. Residential elevation working drawings"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Skills Needed *</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {skills.map((sk) => (
                <label
                  key={sk.skillId}
                  className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer ${
                    selected.includes(sk.skillId)
                      ? "border-flamingo-400 bg-flamingo-50 text-flamingo-800"
                      : "border-gray-200 text-gray-700"
                  } ${priced ? "opacity-60 pointer-events-none" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(sk.skillId)}
                    onChange={() => toggle(sk.skillId)}
                    className="accent-flamingo-500"
                  />
                  {sk.name}
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
            <textarea
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={!!priced}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-flamingo-300 disabled:bg-gray-50"
              placeholder="Describe the scope, deliverables, and any specifics…"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                <Mic size={16} /> Voice Note (optional)
              </label>
              <VoiceRecorder onChange={setVoiceFile} disabled={!!priced} />
              <p className="mt-1 text-xs text-gray-400">Record, play it back, and re-record if needed.</p>
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                <FileArchive size={16} /> Project ZIP (CTB/fonts/drawings)
              </label>
              <input
                type="file"
                accept=".zip"
                disabled={!!priced}
                onChange={(e) => setZipFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-md file:border-0 file:bg-gray-100 file:px-3 file:py-1.5 file:text-sm"
              />
            </div>
          </div>

          {!priced && (
            <button
              onClick={handleGetPrice}
              disabled={!canSubmit}
              className="inline-flex items-center gap-2 rounded-md bg-flamingo-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-flamingo-700 disabled:opacity-50"
            >
              {submitting && <Loader2 size={16} className="animate-spin" />}
              {submitting ? "Pricing…" : "Get Estimate"}
            </button>
          )}
        </div>

        {/* Estimate panel */}
        <div className="space-y-4">
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Estimate</h3>
            {!priced ? (
              <p className="mt-3 text-sm text-gray-400">
                Fill in the details and click “Get Estimate” to see the price.
              </p>
            ) : (
              <>
                <div className="mt-2 text-3xl font-bold text-gray-900">{fmtINR(priced.estimatedPrice)}</div>
                <p className="mt-1 text-xs text-gray-500">
                  Complexity: {priced.priceBreakdown?.complexity || "—"}
                  {priced.priceBreakdown?.used_fallback ? " (standard rates)" : ""}
                </p>
                <div className="mt-4 space-y-1 border-t pt-3">
                  {breakdownLines.map((l) => (
                    <div key={l.skill_id} className="flex justify-between text-xs text-gray-600">
                      <span className="truncate pr-2">{l.skill_name}</span>
                      <span>{fmtINR(l.line_total)}</span>
                    </div>
                  ))}
                </div>
                <button
                  onClick={handleApprove}
                  disabled={approving}
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-md bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  {approving ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                  {approving ? "Approving…" : "Approve & Find Match"}
                </button>
                <p className="mt-2 text-center text-[11px] text-gray-400">
                  Approving holds the amount from your wallet.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default NewProject;
